import * as THREE from "three";
import { facet } from "@rpg/meshkit";
import type { ChainSpec, LinksPiece } from "./card";
import { glyphGeometry, surface, type BuildContext } from "./pieces";

// Spring chains (the secondary-chains suggestion: one spring per link): a scarf or a hood point hangs from a joint as a
// polyline of points stepped with Verlet on a fixed 120 Hz substep: pinned at the anchor, pulled toward the rest shape
// carried by the anchor (stiffness), damped, pulled down (gravity), dragged by the air, and held to link length.
// Every link of a chain is one rigid piece; all of a chain's links draw as ONE mesh, re-posed on the CPU each frame
// (a few hundred vertices), so a 7-link scarf costs one draw call, not seven.

/** Planted defects beside what they break: the pose spec's settle check must fail with `undamped` on. */
export const CHAIN_FAULTS = {
  /** The springs lose their damping and air drag, so a chain never settles. */
  undamped: false,
  /** Every vertex follows its own link rigidly, `bend` ignored: a split hood's point hinges open at its seam. */
  rigidSeam: false,
};

const STEP = 1 / 120;
const _m = new THREE.Matrix4();
const _m2 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _v4 = new THREE.Vector3();
const _q3 = new THREE.Quaternion();
const _s = new THREE.Vector3(1, 1, 1);
const DOWN = new THREE.Vector3(0, -1, 0);

/**
 * Every point of a chain: its rest polyline, then `grow.links` more points carrying on along its last link at that
 * link's length (a scarf that can lengthen: the extra links exist from the start and show as `setLinks` asks).
 */
export function chainPoints(spec: ChainSpec): [number, number, number][] {
  const pts = spec.points.map((p) => [...p] as [number, number, number]);
  const extra = Math.max(0, Math.floor(spec.grow?.links ?? 0));
  if (!extra || pts.length < 2) return pts;
  const a = pts[pts.length - 2]!;
  const b = pts[pts.length - 1]!;
  const d = [b[0] - a[0], b[1] - a[1], b[2] - a[2]] as const;
  for (let i = 1; i <= extra; i++) pts.push([b[0] + d[0] * i, b[1] + d[1] * i, b[2] + d[2] * i]);
  return pts;
}

/** Link i's rest matrix in model space: at points[i], local -Y along the link, local +Z toward `facing`. */
export function linkRestMatrix(spec: ChainSpec, i: number): THREE.Matrix4 {
  const all = chainPoints(spec);
  const a = new THREE.Vector3(...all[i]!);
  const b = new THREE.Vector3(...all[i + 1]!);
  const y = a.clone().sub(b).normalize();
  const f = new THREE.Vector3(...(spec.facing ?? [0, 0, -1]));
  const z = f.clone().addScaledVector(y, -f.dot(y));
  if (z.lengthSq() < 1e-8) z.set(1, 0, 0).addScaledVector(y, -y.x);
  z.normalize();
  const x = new THREE.Vector3().crossVectors(y, z).normalize();
  return new THREE.Matrix4().makeBasis(x, y, z).setPosition(a);
}

/** The geometry a `links` piece puts on every link of its chain, in model space (rest pose). */
export function linksGeometry(p: LinksPiece, spec: ChainSpec, ctx: BuildContext): { link: number; geo: THREE.BufferGeometry }[] {
  const out: { link: number; geo: THREE.BufferGeometry }[] = [];
  const all = chainPoints(spec);
  for (let i = 0; i < all.length - 1; i++) {
    const rest = linkRestMatrix(spec, i);
    const len = new THREE.Vector3(...all[i]!).distanceTo(new THREE.Vector3(...all[i + 1]!));
    const parts: THREE.BufferGeometry[] = [facet(new THREE.BoxGeometry(p.width, len, p.thickness).translate(0, -len / 2, 0), { ...surface(p, ctx, { jitter: 0.003, tint: 0.05 }) })];
    if (p.edge) {
      const e = p.edge;
      for (const ex of [-1, 1])
        parts.push(facet(new THREE.BoxGeometry(e.width, len, p.thickness * 1.3).translate((ex * (p.width - e.width)) / 2, -len / 2, 0), { colour: ctx.colour(e.colour), rng: ctx.rng }));
    }
    if (p.glyphs?.shapes.length) {
      const g = p.glyphs;
      const shape = g.shapes[i % g.shapes.length]!;
      for (const z of [1, -1])
        for (const gg of glyphGeometry(shape, g.size)) parts.push(facet(gg.translate(0, -len / 2, (z * p.thickness) / 2 + z * 0.002), { colour: ctx.colour(g.colour), glow: g.glow, rng: ctx.rng }));
    }
    for (const geo of parts) out.push({ link: i, geo: geo.applyMatrix4(rest) });
  }
  return out;
}

/** What a chain reports: how fast its points move and how far they hang from rest. */
export interface ChainStats {
  /** m/s: the fastest point in the last substep. */
  speed: number;
  /** Metres: the farthest point from its rest target. */
  swing: number;
  /** Metres: the free end's distance from where it hangs at rest. */
  tipOffset: number;
}

/** A chain: its simulation and its one mesh. */
export interface Chain {
  spec: ChainSpec;
  mesh: THREE.Mesh;
  /** Step the springs by `dt` seconds (fixed substeps) and re-pose the mesh. The anchor's world matrix must be current. */
  update(dt: number): void;
  /** Snap every point to its rest target (after a teleport). */
  reset(): void;
  stats(): ChainStats;
  /** The points, world space. */
  points(): THREE.Vector3[];
  /** Put the points where something else says (world space, one per point: a ragdoll's chain bodies) and re-pose the
   *  mesh; the springs pick up from there on the next `update`. Given only the rest polyline's points, a grown
   *  chain's extra points carry on along its last link. */
  follow(points: readonly THREE.Vector3[]): void;
  /** How many links show (the rest polyline's to all of them with `grow`): a scarf lengthening. The hidden tail still
   *  hangs (it never pulls on the shown links), so a link shown later appears where it would have been. */
  setLinks(n: number): void;
  /** The links showing, and the most there can be. */
  links(): { shown: number; max: number; base: number };
}

/**
 * A chain over `spec`, its links' geometry (model space, per link) merged into one mesh under `root`, hanging from
 * `anchor` (a joint whose rest model matrix is `anchorRest`).
 */
export function createChain(spec: ChainSpec, linkGeos: { link: number; geo: THREE.BufferGeometry }[], material: THREE.Material, root: THREE.Object3D, anchor: THREE.Object3D, anchorRest: THREE.Matrix4): Chain {
  const all = chainPoints(spec);
  const n = all.length;
  const links = n - 1;
  const baseLinks = spec.points.length - 1;
  const restInv = Array.from({ length: links }, (_, i) => linkRestMatrix(spec, i).invert());
  const restRot = Array.from({ length: links }, (_, i) => new THREE.Quaternion().setFromRotationMatrix(linkRestMatrix(spec, i)));
  // link i's rest frame in link i - 1's (a bending vertex of link i starts from the previous link's pose)
  const rel = Array.from({ length: links }, (_, i) => (i === 0 ? new THREE.Matrix4() : restInv[i - 1]!.clone().multiply(linkRestMatrix(spec, i))));
  const restDir = Array.from({ length: links }, (_, i) => new THREE.Vector3(...all[i + 1]!).sub(new THREE.Vector3(...all[i]!)).normalize());
  const lengths = Array.from({ length: links }, (_, i) => new THREE.Vector3(...all[i]!).distanceTo(new THREE.Vector3(...all[i + 1]!)));
  const anchorRestInv = anchorRest.clone().invert();
  const restLocal = all.map((p) => new THREE.Vector3(...p).applyMatrix4(anchorRestInv));

  // the mesh: every link's vertices kept in link space, re-posed into root space each frame
  const sorted = [...linkGeos].sort((a, b) => a.link - b.link);
  const counts = sorted.map((l) => l.geo.getAttribute("position").count);
  const total = counts.reduce((a, b) => a + b, 0);
  const localPos = new Float32Array(total * 3);
  const localNor = new Float32Array(total * 3);
  const linkOf = new Uint16Array(total);
  // a vertex's `bend` (a geometry attribute, default 1): how far it follows its own link rather than the frame before
  // it (the anchor, unswung, for link 0; the previous link for the rest), so a piece cut from its joint's mesh (a
  // hood's point) bends from the cut instead of hinging open there
  const bendW = new Float32Array(total).fill(1);
  let bends = false;
  const geo = new THREE.BufferGeometry();
  const col = new Float32Array(total * 3);
  const glow = new Float32Array(total);
  let o = 0;
  for (const { link, geo: g } of sorted) {
    const P = g.getAttribute("position");
    const N = g.getAttribute("normal");
    const C = g.getAttribute("color");
    const G = g.getAttribute("glow");
    const B = g.getAttribute("bend");
    const nm = new THREE.Matrix3().getNormalMatrix(restInv[link]!);
    for (let i = 0; i < P.count; i++, o++) {
      if (B && !CHAIN_FAULTS.rigidSeam) {
        bendW[o] = B.getX(i);
        bends = true;
      }
      _v.fromBufferAttribute(P, i).applyMatrix4(restInv[link]!);
      localPos.set([_v.x, _v.y, _v.z], o * 3);
      _v.fromBufferAttribute(N, i).applyMatrix3(nm).normalize();
      localNor.set([_v.x, _v.y, _v.z], o * 3);
      col.set([C.getX(i), C.getY(i), C.getZ(i)], o * 3);
      glow[o] = G ? G.getX(i) : 0;
      linkOf[o] = link;
    }
  }
  geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(total * 3), 3).setUsage(THREE.DynamicDrawUsage));
  geo.setAttribute("normal", new THREE.BufferAttribute(new Float32Array(total * 3), 3).setUsage(THREE.DynamicDrawUsage));
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  geo.setAttribute("glow", new THREE.BufferAttribute(glow, 1));
  const mesh = new THREE.Mesh(geo, material);
  mesh.name = spec.id;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  root.add(mesh);

  const p = all.map(() => new THREE.Vector3());
  const prev = all.map(() => new THREE.Vector3());
  const target = all.map(() => new THREE.Vector3());
  const lastTarget = all.map(() => new THREE.Vector3());
  // the links showing: the vertices of links 0..shown-1 come first in the buffer (sorted by link)
  const vertsTo = new Array<number>(links + 1).fill(0);
  {
    let acc = 0;
    let li = 0;
    for (let k = 0; k < sorted.length; k++) {
      while (li < sorted[k]!.link) vertsTo[++li] = acc;
      acc += counts[k]!;
    }
    while (li < links) vertsTo[++li] = acc;
    vertsTo[links] = acc;
  }
  let shown = baseLinks;
  geo.setDrawRange(0, vertsTo[shown]!);
  let primed = false;
  let acc = 0;
  let maxSpeed = 0;

  const targets = (out: THREE.Vector3[]): void => {
    for (let k = 0; k < n; k++) out[k]!.copy(restLocal[k]!).applyMatrix4(anchor.matrixWorld);
  };
  const reset = (): void => {
    targets(target);
    for (let k = 0; k < n; k++) {
      p[k]!.copy(target[k]!);
      prev[k]!.copy(target[k]!);
      lastTarget[k]!.copy(target[k]!);
    }
    primed = true;
    acc = 0;
  };

  const step = (f: number): void => {
    const s = spec.spring;
    const damping = CHAIN_FAULTS.undamped ? 0 : s.damping;
    const drag = CHAIN_FAULTS.undamped ? 0 : s.drag;
    maxSpeed = 0;
    // the anchor's targets, interpolated across this frame's substeps
    const t = (k: number) => _v2.copy(lastTarget[k]!).lerp(target[k]!, f);
    p[0]!.copy(t(0));
    prev[0]!.copy(p[0]!);
    for (let k = 1; k < n; k++) {
      const pk = p[k]!;
      const vel = _v.copy(pk).sub(prev[k]!);
      maxSpeed = Math.max(maxSpeed, vel.length() / STEP);
      vel.multiplyScalar(1 - damping);
      prev[k]!.copy(pk);
      // drag toward still air, then gravity
      vel.multiplyScalar(Math.max(0, 1 - drag * STEP));
      pk.add(vel).addScaledVector(DOWN, s.gravity * STEP * STEP);
      pk.lerp(t(k), s.stiffness);
    }
    // hold each link's length, root first
    for (let it = 0; it < 2; it++)
      for (let k = 1; k < n; k++) {
        const d = _v.copy(p[k]!).sub(p[k - 1]!);
        const len = d.length() || 1e-6;
        p[k]!.copy(p[k - 1]!).addScaledVector(d, lengths[k - 1]! / len);
      }
    // keep each link within maxAngle of its rest direction as the anchor carries it, root first
    const max = s.maxAngle;
    if (max !== undefined)
      for (let k = 1; k < n; k++) {
        const from = _v3.copy(lastTarget[k - 1]!).lerp(target[k - 1]!, f);
        const rest = _v4.copy(lastTarget[k]!).lerp(target[k]!, f).sub(from).normalize();
        const d = _v.copy(p[k]!).sub(p[k - 1]!).normalize();
        const angle = Math.acos(THREE.MathUtils.clamp(d.dot(rest), -1, 1));
        if (angle <= max) continue;
        _q3.setFromUnitVectors(rest, d);
        _q.identity().slerp(_q3, max / angle);
        p[k]!.copy(p[k - 1]!).addScaledVector(rest.applyQuaternion(_q), lengths[k - 1]!);
      }
  };

  const pose = (): void => {
    _m2.copy(root.matrixWorld).invert();
    const anchorQ = anchor.getWorldQuaternion(_q2);
    const P = geo.getAttribute("position") as THREE.BufferAttribute;
    const N = geo.getAttribute("normal") as THREE.BufferAttribute;
    const mats: THREE.Matrix4[] = [];
    const froms: THREE.Matrix4[] = [];
    for (let i = 0; i < links; i++) {
      // rest orientation carried by the anchor, then swung so -Y follows the link's current direction
      const rest = _q.copy(anchorQ).multiply(restRot[i]!);
      const restWorldDir = _v.copy(restDir[i]!).applyQuaternion(anchorQ);
      const cur = _v2.copy(p[i + 1]!).sub(p[i]!).normalize();
      const swing = new THREE.Quaternion().setFromUnitVectors(restWorldDir, cur);
      // the frame a bending vertex starts from: link 0's unswung pose at its pinned root; else the previous link's
      // pose carried to this link's rest
      if (bends) froms[i] = i === 0 ? new THREE.Matrix4().compose(p[0]!, rest, _s).premultiply(_m2) : mats[i - 1]!.clone().multiply(rel[i]!);
      const q = swing.multiply(rest);
      mats.push(new THREE.Matrix4().compose(p[i]!, q, _s).premultiply(_m2));
    }
    const nms = mats.map((m) => new THREE.Matrix3().setFromMatrix4(m));
    const fnms = bends ? froms.map((m) => new THREE.Matrix3().setFromMatrix4(m)) : [];
    const pa = P.array as Float32Array;
    const na = N.array as Float32Array;
    for (let v = 0; v < total; v++) {
      const m = mats[linkOf[v]!]!.elements;
      const x = localPos[v * 3]!;
      const y = localPos[v * 3 + 1]!;
      const z = localPos[v * 3 + 2]!;
      let px = m[0]! * x + m[4]! * y + m[8]! * z + m[12]!;
      let py = m[1]! * x + m[5]! * y + m[9]! * z + m[13]!;
      let pz = m[2]! * x + m[6]! * y + m[10]! * z + m[14]!;
      const r = nms[linkOf[v]!]!.elements;
      const nx = localNor[v * 3]!;
      const ny = localNor[v * 3 + 1]!;
      const nz = localNor[v * 3 + 2]!;
      let qx = r[0]! * nx + r[3]! * ny + r[6]! * nz;
      let qy = r[1]! * nx + r[4]! * ny + r[7]! * nz;
      let qz = r[2]! * nx + r[5]! * ny + r[8]! * nz;
      const w = bendW[v]!;
      if (w < 1) {
        const a = froms[linkOf[v]!]!.elements;
        const b = fnms[linkOf[v]!]!.elements;
        px = (a[0]! * x + a[4]! * y + a[8]! * z + a[12]!) * (1 - w) + px * w;
        py = (a[1]! * x + a[5]! * y + a[9]! * z + a[13]!) * (1 - w) + py * w;
        pz = (a[2]! * x + a[6]! * y + a[10]! * z + a[14]!) * (1 - w) + pz * w;
        qx = (b[0]! * nx + b[3]! * ny + b[6]! * nz) * (1 - w) + qx * w;
        qy = (b[1]! * nx + b[4]! * ny + b[7]! * nz) * (1 - w) + qy * w;
        qz = (b[2]! * nx + b[5]! * ny + b[8]! * nz) * (1 - w) + qz * w;
      }
      pa[v * 3] = px;
      pa[v * 3 + 1] = py;
      pa[v * 3 + 2] = pz;
      na[v * 3] = qx;
      na[v * 3 + 1] = qy;
      na[v * 3 + 2] = qz;
    }
    P.needsUpdate = true;
    N.needsUpdate = true;
    // bounds: around the points, in root space
    const c = new THREE.Vector3();
    for (const q of p) c.add(q);
    c.multiplyScalar(1 / n).applyMatrix4(_m2);
    geo.boundingSphere = new THREE.Sphere(c, lengths.reduce((a, b) => a + b, 0) + 0.3);
  };

  return {
    spec,
    mesh,
    update(dt) {
      if (!primed) reset();
      targets(target);
      acc += Math.min(dt, 0.25);
      const steps = Math.floor(acc / STEP);
      acc -= steps * STEP;
      for (let i = 1; i <= steps; i++) step(i / steps);
      for (let k = 0; k < n; k++) lastTarget[k]!.copy(target[k]!);
      p[0]!.copy(target[0]!);
      // hold each link's length from the pinned root (a no-op after a step; after a re-pin with no step, the chain follows)
      if (!steps) for (let k = 1; k < n; k++) p[k]!.sub(p[k - 1]!).setLength(lengths[k - 1]!).add(p[k - 1]!);
      pose();
    },
    reset() {
      reset();
      pose();
    },
    stats() {
      let swing = 0;
      for (let k = 0; k < n; k++) swing = Math.max(swing, p[k]!.distanceTo(target[k]!));
      return { speed: maxSpeed, swing, tipOffset: p[shown]!.distanceTo(target[shown]!) };
    },
    points: () => p.map((q) => q.clone()),
    follow(points) {
      const base = spec.points.length;
      if (points.length !== n && points.length !== base) throw new Error(`@rpg/figure: chain "${spec.id}" has ${n} points, given ${points.length}`);
      for (let k = 0; k < points.length; k++) {
        p[k]!.copy(points[k]!);
        prev[k]!.copy(points[k]!);
      }
      // a grown chain's tail carries on along the last given link
      if (points.length < n && points.length >= 2) {
        const d = _v.copy(points[points.length - 1]!).sub(points[points.length - 2]!);
        for (let k = points.length; k < n; k++) {
          p[k]!.copy(p[k - 1]!).add(d);
          prev[k]!.copy(p[k]!);
        }
      }
      primed = true;
      pose();
    },
    setLinks(want) {
      shown = Math.max(1, Math.min(links, Math.floor(want)));
      geo.setDrawRange(0, vertsTo[shown]!);
    },
    links: () => ({ shown, max: links, base: baseLinks }),
  };
}

/** The world matrix of a joint's rest pose in model space: a translation (skeleton joints carry no rest turn). */
export function restMatrix(at: [number, number, number]): THREE.Matrix4 {
  return _m.clone().makeTranslation(at[0], at[1], at[2]);
}
